import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import Draggable from "gsap/Draggable";
import InertiaPlugin from "gsap/InertiaPlugin";
import imagesLoaded from "imagesloaded";
import tippy from "tippy.js";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, Draggable, InertiaPlugin);

/* -------------------------------------------------- */
/* 🧑 Header */
/* -------------------------------------------------- */

// Add class to header after scrolling 50px
gsap.to("header", {
  scrollTrigger: {
    start: 45,
    end: 999999999,
    trigger: "header",
    toggleClass: "scrolled",
  },
});

/* -------------------------------------------------- */
/* 🌇 Splash screen */
/* -------------------------------------------------- */

if (document.querySelector(".splash_bg")) {
  // Fade out on scroll
  gsap.to(".splash_bg", {
    autoAlpha: 0,
    scrollTrigger: {
      scrub: true,
      trigger: ".splash_bg",
      start: "top top",
      end: "bottom top",
    },
  });
}

/* -------------------------------------------------- */
/* ⚓ Anchor links */
/* -------------------------------------------------- */

document.querySelectorAll('a[href^="#"]:not(.share)').forEach((anchor) => {
  // Smooth scrolling with GSAP ScrollToPlugin
  anchor.addEventListener("click", function (e) {
    let href = anchor.getAttribute("href");
    let element = false;

    // Only for anchor links that are not #, #! or #menu
    if (
      href.startsWith("#") &&
      href != "#!" &&
      href != "#" &&
      href != "#menu"
    ) {
      element = document.querySelector(href);
    }

    // Only for anchor links with a valid target
    if (element) {
      // Different vertical offsets for Desktop and Mobile
      let offsetY = 0;
      if (window.innerWidth < 1000) {
        offsetY = 64;
      }

      // Smoothly scroll to the element
      gsap.to(window, {
        scrollTo: {
          y: element,
          offsetY,
        },
        duration: 0.5,
        ease: "power1.inOut",
      });

      // Update the URL
      history.pushState(null, null, anchor.getAttribute("href"));

      // Prevent the default behavior
      e.preventDefault();
    }
  });
});

/* -------------------------------------------------- */
/* ❓ Question menu */
/* -------------------------------------------------- */

if (document.querySelector(".question_menu")) {
  // Animate in on scroll
  gsap.fromTo(
    ".question_menu",
    {
      yPercent: 100,
    },
    {
      yPercent: 0,
      autoAlpha: 1,
      scrollTrigger: {
        scrub: true,
        trigger: "#frage-1",
        start: "top bottom",
        end: "top center",
      },
    }
  );
}

/* -------------------------------------------------- */
/* 📋 Questions form  */
/* -------------------------------------------------- */

const form = document.querySelector("form#questions");
const questions = document.querySelectorAll(".question");
const questionNumber = document.querySelector(".question_number");
const questionNext = document.querySelector(".question_next .button");
const preview = document.querySelector(".preview");

if (form) {
  // Listen for key presses
  document.addEventListener("keydown", (e) => {
    // Space, ArrowDown, ArrowRight
    if (e.key === " " || e.key === "ArrowDown" || e.key === "ArrowRight") {
      let nextDot;

      if ((nextDot = document.querySelector(".dot:not(.is_open)"))) {
        // If there is a dot that is not open, click it
        nextDot.click();

        // Prevent the default behavior
        e.preventDefault();
      }
    }

    // ArrowUp, ArrowLeft
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      let openDots = document.querySelectorAll(".dot.is_open");

      if (openDots.length >= 2) {
        // If there are at least two open dots, click the second last one
        openDots[openDots.length - 2].click();

        // Prevent the default behavior
        e.preventDefault();
      }
    }
  });

  // Handle the form submission to get the preview (car images)
  function submitForm() {
    // Animate out the car images if there are any
    if (preview.querySelectorAll(".car_image").length) {
      gsap.to(preview.querySelectorAll(".car_image"), {
        xPercent: 5,
        autoAlpha: 0,
        stagger: 0.1,
        duration: 0.25,
      });
    }

    // Get the form data and send it to the server
    let formData = new FormData(form);
    fetch(form.action, {
      method: "post",
      body: formData,
    })
      .then(function (response) {
        // Get the HTML response
        return response.text();
      })
      .then(function (html) {
        // Parse the HTML response
        let parser = new DOMParser();
        let doc = parser.parseFromString(html, "text/html");
        let carImages = doc.querySelectorAll(".car_image");

        // If there are any car images, add them to the preview
        if (carImages.length) {
          // Remove the old car images
          preview.innerHTML = "";

          // Add the new car images invisibly
          carImages.forEach(function (carImage) {
            gsap.set(carImage, {
              autoAlpha: 0,
              xPercent: 5,
            });
            preview.appendChild(carImage);
          });

          // After the images loaded, animate them in
          imagesLoaded(carImages, function (instance) {
            gsap.to(preview.querySelectorAll(".car_image"), {
              xPercent: 0,
              autoAlpha: 1,
              stagger: 0.1,
              duration: 0.25,
            });
          });
        }
      });
  }

  // Submit the form on page load
  submitForm();

  // Listen for clicks on the next button
  questionNext.addEventListener("click", function (e) {
    // If the next button is the results button, submit the form
    if (
      questionNext.getAttribute("href") ==
      questionNext.getAttribute("data-results")
    ) {
      form.submit();
      e.preventDefault();
    }
  });

  // Handle the questions
  questions.forEach((question) => {
    // Find the dot for this question
    let dot = document.querySelector(
      'a.dot[href="#' + question.getAttribute("id") + '"]'
    );

    // Scroll animation for Desktop only
    ScrollTrigger.matchMedia({
      "(min-height: 800px) and (min-width: 1000px)": function () {
        // Animate the question in
        gsap.from(question.querySelector(".inner"), {
          autoAlpha: 0,
          scale: 0.75,
          scrollTrigger: {
            scrub: true,
            trigger: question,
            start: "top center",
            end: "top top",
          },
        });

        // Kill the animation if the media query doesn't match anymore
        return function () {
          gsap.set(question.querySelector(".inner"), {
            autoAlpha: 1,
            scale: 1,
          });
        };
      },
    });

    // Handle the dot active state, question number and next button
    ScrollTrigger.create({
      trigger: question,
      scrub: true,
      start: "top center",
      end: "bottom center",

      // Enter ⬇️
      onEnter: function () {
        // Add the active state to the dot
        dot.classList.add("is_open");

        // Set the question number
        gsap.set(questionNumber, {
          innerHTML: question.getAttribute("data-question"),
        });

        // Find the next question by adding 1 to the current question number
        let nextId = parseFloat(question.getAttribute("data-question")) + 1;

        // If there is a next question, set the next button to "Weiter"
        if (document.querySelector("#frage-" + nextId)) {
          gsap.set(questionNext, {
            href: "#frage-" + nextId,
            innerHTML: "Weiter",
          });
        }

        // If there is no next question, set the next button to "Ergebnis"
        else {
          gsap.set(questionNext, {
            href: questionNext.getAttribute("data-results"),
            innerHTML: "Ergebnis",
          });
        }
      },

      // Enter ⬆️
      onEnterBack: function () {
        // Set the question number
        gsap.set(questionNumber, {
          innerHTML: question.getAttribute("data-question"),
        });

        // Find the next question by adding 1 to the current question number
        let nextId = parseFloat(question.getAttribute("data-question")) + 1;

        // If there is a next question, set the next button to "Weiter"
        if (document.querySelector("#frage-" + nextId)) {
          gsap.set(questionNext, {
            href: "#frage-" + nextId,
            innerHTML: "Weiter",
          });
        }

        // If there is no next question, set the next button to "Ergebnis"
        else {
          gsap.set(questionNext, {
            href: questionNext.getAttribute("data-results"),
            innerHTML: "Ergebnis",
          });
        }
      },

      // Leave ⬆️
      onLeaveBack: function () {
        // Remove the active state from the dot
        dot.classList.remove("is_open");
      },
    });

    // Handle the answers
    let answers = question.querySelectorAll(".answer_wrap");
    answers.forEach(function (answer) {
      // Find the radio button
      let answerInput = answer.querySelector("input");

      // Handle the click on the answer
      answer.addEventListener("click", function (e) {
        // Toggle the radio button
        answerInput.checked = !answerInput.checked;

        // If the radio button is checked, add the active state to the dot and question
        if (answerInput.checked) {
          dot.classList.add("is_done");
          question.classList.add("is_done");
        }

        // If the radio button is not checked, remove the active state from the dot and question
        else {
          dot.classList.remove("is_done");
          question.classList.remove("is_done");
        }

        // Submit the form
        submitForm();

        // Prevent the default behavior
        e.preventDefault();
      });
    });

    // Handle the priorities
    let priorities = question.querySelectorAll(".priority_wrap");
    priorities.forEach(function (priority) {
      // Find the priority checkbox
      let priorityInput = priority.querySelector("input");

      // Handle the click on the priority
      priority.addEventListener("click", function (e) {
        // Toggle the priority checkbox
        priorityInput.checked = !priorityInput.checked;

        // If the priority checkbox is checked, add the priority state to the dot
        if (priorityInput.checked) {
          dot.classList.add("is_priority");
        }

        // If the priority checkbox is not checked, remove the priority state from the dot
        else {
          dot.classList.remove("is_priority");
        }

        // Submit the form
        submitForm();

        // Prevent the default behavior
        e.preventDefault();
      });
    });
  });
}

/* -------------------------------------------------- */
/* ✨ Results */
/* -------------------------------------------------- */

if (document.querySelector(".results")) {
  // Animate the cars in one by one
  gsap.fromTo(
    ".results .car",
    {
      scale: 0.9,
    },
    {
      autoAlpha: 1,
      scale: 1,
      stagger: 0.25,
    }
  );

  // Handle the car feature list scrolling
  window.addEventListener("load", function () {
    let carFeatures = document.querySelectorAll(".car_features_scroll");
    carFeatures.forEach((carFeature) => {
      let minX =
        carFeature.closest(".car_features_list").scrollWidth -
        carFeature.clientWidth -
        20;
      Draggable.create(carFeature, {
        type: "x",
        inertia: true,
        bounds: { minX: minX * -1, maxX: 0 },
      });
    });
  });
}

/* -------------------------------------------------- */
/* 🌄 Image modules */
/* -------------------------------------------------- */

// Find all image and call to action modules
// let imageModules = document.querySelectorAll(".module__image, .module__c2a");
// if (imageModules) {
//   imageModules.forEach(function (imageModule) {

//     // Parallax effect for the image
//     let image = imageModule.querySelector("img");
//     gsap.to(image, {
//       scrollTrigger: {
//         trigger: imageModule,
//         start: "top bottom",
//         end: "bottom top",
//         scrub: true,
//       },
//       ease: "linear",
//       yPercent: 25,
//     });

//   });
// }

/* -------------------------------------------------- */
/* 💡 Icon modules */
/* -------------------------------------------------- */

// Find all icon modules
let iconColumns = document.querySelectorAll(".icon_columns");
if (iconColumns) {
  iconColumns.forEach(function (iconColumn) {
    // Animate the icons in one by one
    let iconAnimation = gsap.to(iconColumn.querySelectorAll(".icon_column"), {
      autoAlpha: 1,
      duration: 1,
      stagger: 0.5,
    });

    // Play the animation when the page is loaded
    window.addEventListener("load", function () {
      iconAnimation.play();
    });
  });
}

/* -------------------------------------------------- */
/* 📜 Scroll menu */
/* -------------------------------------------------- */

// Find all modules
const modules = document.querySelectorAll(".module");

if (modules.length) {
  modules.forEach(function (module) {
    let id = module.getAttribute("id");

    // Find the link in the menu
    let link = document.querySelector('.menu a[href="#' + id + '"]');

    // Animate the link in when the module is in view
    if (link) {
      ScrollTrigger.create({
        trigger: module,
        start: "top-=100 top",
        end: "bottom-=100 top",
        onEnter: () => {
          link.classList.add("is_open");
        },
        onEnterBack: () => {
          link.classList.add("is_open");
        },
        onLeave: () => {
          link.classList.remove("is_open");
        },
        onLeaveBack: () => {
          link.classList.remove("is_open");
        },
      });
    }
  });
}

/* -------------------------------------------------- */
/* ⭐ Favourites */
/* -------------------------------------------------- */

let favoriteButtons = document.querySelectorAll(".car_favorite");

if (favoriteButtons.length) {
  favoriteButtons.forEach(function (favoriteButton) {
    favoriteButton.tippy = tippy(favoriteButton.closest(".car_image"), {
      content: favoriteButton.getAttribute("data-text"),
      placement: "bottom",
      animation: "shift-toward",
      theme: "mobility4.me",
      inertia: true,
      maxWidth: 350,
      offset: [0, 15],
    });
  });

  if (!document.querySelector(".form_overlay")) {
    setTimeout(function () {
      favoriteButtons[0].tippy.show();

      setTimeout(function () {
        favoriteButtons[0].tippy.hide();
      }, 2000);
    }, 1500);
  }
}

/* -------------------------------------------------- */
/* 🎭 Optional Form */
/* -------------------------------------------------- */

let formButton = document.querySelector(".button--show-form");

if (formButton) {
  let formOverlay = document.querySelector(".form_overlay");
  let formOverlayInner = document.querySelector(".form_overlay_inner");

  formButton.addEventListener("click", function () {
    gsap.to(formOverlay, {
      autoAlpha: 1,
      duration: 0.25,
    });

    gsap.from(formOverlayInner, {
      scale: 0.85,
      ease: "power4.out",
      onStart: () => {
        formOverlayInner.querySelector("input").focus();
      },
    });
  });

  formOverlay.addEventListener("click", function (e) {
    if (!e.target.matches(".form_overlay_inner, .form_overlay_inner *")) {
      gsap.to(formOverlay, {
        autoAlpha: 0,
        duration: 0.25,
      });
    }
  });

  formOverlay.addEventListener("keydown", function (e) {
    if (e.key === "Escape") {
      gsap.to(formOverlay, {
        autoAlpha: 0,
        duration: 0.25,
      });
    }
  });
}
